import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareText, Copy, Send, Clock, Mail, Pencil } from "lucide-react";
import { NoticeTemplateEditor } from "@/components/NoticeTemplateEditor";
import { toast } from "sonner";

const TEMPLATES = [
  { key: "rent_due", label: "Rent Due", desc: "Friendly reminder that rent is due", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "rent_late", label: "Rent Late", desc: "Past due / 5-day notice", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "pay_or_quit", label: "Pay or Quit", desc: "Formal pay-or-quit notice", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  { key: "rental_increase", label: "Rental Increase", desc: "Notice of rent adjustment", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { key: "late_fee", label: "Late Fee Notice", desc: "Explains a late fee on the balance", tone: "bg-orange-50 text-orange-700 border-orange-200" },
  { key: "rent_due_soon", label: "Rent Due Soon", desc: "Auto nudge sent by email + text before the due date", tone: "bg-teal-50 text-teal-700 border-teal-200" },
];

export default function Messages() {
  const [leases, setLeases] = useState(null);
  const [log, setLog] = useState([]);
  const [leaseId, setLeaseId] = useState("");
  const [template, setTemplate] = useState("rent_due");
  const [newAmount, setNewAmount] = useState("");
  const [effective, setEffective] = useState("");
  const [custom, setCustom] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [customKeys, setCustomKeys] = useState([]);
  const [previewKey, setPreviewKey] = useState(0);

  const loadLog = useCallback(() => api.get("/messages/log").then((r) => setLog(r.data)), []);
  const loadTemplates = useCallback(() => api.get("/notice-templates")
    .then((r) => setCustomKeys(r.data.templates.filter((t) => t.is_custom).map((t) => t.template_type)))
    .catch((e) => console.warn("templates load failed", e?.message)), []);
  useEffect(() => {
    api.get("/leases").then((r) => {
      const active = r.data.filter((l) => l.status === "active");
      setLeases(active);
      if (active.length) setLeaseId(active[0].id);
    });
    loadLog();
    loadTemplates();
  }, [loadLog, loadTemplates]);

  useEffect(() => {
    if (!leaseId) return;
    const params = { template_type: template, custom_note: custom };
    if (newAmount) params.new_amount = Number(newAmount);
    if (effective) params.effective_date = effective;
    api.get(`/leases/${leaseId}/message-preview`, { params }).then((r) => setPreview(r.data))
      .catch((e) => console.warn("preview failed", e?.message));
  }, [leaseId, template, newAmount, effective, custom, previewKey]);

  const onTemplateSaved = () => { setPreviewKey((k) => k + 1); loadTemplates(); };

  const send = async () => {
    setSending(true);
    try {
      const payload = { lease_id: leaseId, template_type: template, custom_note: custom };
      if (newAmount) payload.new_amount = Number(newAmount);
      if (effective) payload.effective_date = effective;
      const { data } = await api.post("/messages/send", payload);
      toast.success(`Email sent to ${data.to}`);
      loadLog();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  const copy = () => {
    if (preview?.text) { navigator.clipboard.writeText(preview.text); toast.success("Message copied to clipboard"); }
  };

  if (!leases) return <Loading />;
  if (leases.length === 0)
    return (
      <div><PageHeader title="Notices" subtitle="Send professional notices to your tenants." />
        <EmptyState icon={MessageSquareText} title="No active leases" subtitle="Add a tenant with a lease to send notices." />
      </div>
    );

  return (
    <div>
      <PageHeader title="Notices" subtitle="Pre-written professional notices — edit the wording, preview, copy, or email instantly. Sent notices also appear in the tenant's Inbox thread." />

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <Label className="text-xs">Tenant / lease</Label>
            <Select value={leaseId} onValueChange={setLeaseId}>
              <SelectTrigger className="mt-1.5 mb-4" data-testid="msg-lease"><SelectValue /></SelectTrigger>
              <SelectContent>{leases.map((l) => <SelectItem key={l.id} value={l.id}>{l.tenant?.name} · {l.property?.name}</SelectItem>)}</SelectContent>
            </Select>

            <div className="flex items-center justify-between mt-4 mb-1.5">
              <Label className="text-xs">Template</Label>
              <button onClick={() => setEditing(true)} data-testid="edit-template-btn"
                className="text-xs font-semibold text-primary flex items-center gap-1 hover:underline"><Pencil className="h-3 w-3" /> Edit wording</button>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {TEMPLATES.map((t) => (
                <button key={t.key} onClick={() => setTemplate(t.key)} data-testid={`template-${t.key}`}
                  className={`text-left rounded-xl border p-3 transition relative ${template === t.key ? t.tone + " ring-2 ring-offset-1 ring-current" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="text-sm font-semibold text-slate-900">{t.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{t.desc}</div>
                  {customKeys.includes(t.key) && <span className="absolute top-2 right-2 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-bold px-1.5 py-0.5" data-testid={`template-custom-${t.key}`}>EDITED</span>}
                </button>
              ))}
            </div>

            {template === "rental_increase" && (
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="space-y-1.5"><Label className="text-xs">New rent</Label><Input type="number" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} placeholder="1750" data-testid="msg-new-amount" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Effective date</Label><Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} /></div>
              </div>
            )}

            <div className="space-y-1.5 mt-4">
              <Label className="text-xs">Add a personal note (optional)</Label>
              <Textarea value={custom} onChange={(e) => setCustom(e.target.value)} rows={2} placeholder="Anything you'd like to add…" />
            </div>

            <div className="flex gap-2 mt-5">
              <Button variant="outline" className="flex-1 gap-2" onClick={copy} data-testid="msg-copy"><Copy className="h-4 w-4" /> Copy</Button>
              <Button className="flex-1 gap-2" onClick={send} disabled={sending} data-testid="msg-send"><Send className="h-4 w-4" /> {sending ? "Sending…" : "Send email"}</Button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="msg-preview">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
              <Mail className="h-4 w-4" /> To: <span className="font-semibold text-slate-700">{preview?.tenant_email || "—"}</span>
            </div>
            <div className="font-display font-bold text-slate-900 border-b border-slate-200 pb-3">{preview?.subject || "…"}</div>
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed mt-4">{preview?.text || "Select a template to preview."}</pre>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display font-bold text-slate-900 mb-3 flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> Recently sent</h3>
            {log.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">No messages sent yet.</p> : (
              <div className="space-y-2">
                {log.slice(0, 8).map((m) => (
                  <div key={m.id || m.created_at} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                    <span className="text-slate-700">{m.subject}</span>
                    <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleDateString()}{m.auto ? " · auto" : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <NoticeTemplateEditor templateType={template} open={editing} onOpenChange={setEditing} onSaved={onTemplateSaved} />
    </div>
  );
}
